import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { invalidateLoanActivity } from '@/hooks/lib/invalidate-loan-activity';
import { api } from '@/lib/api';

export interface LoanDocument {
  id: string;
  loanId: string;
  name: string;
  type: string;
  category: string;
  fileUrl: string;
  fileSize: number;
  mimeType: string;
  createdAt: string;
  updatedAt: string;
  uploadedBy?: { id: string; firstName: string; lastName: string };
}

export interface DocumentCategory {
  key: string;
  label: string;
}

export function useDocuments(loanId: string) {
  return useQuery({
    queryKey: ['loans', loanId, 'documents'],
    queryFn: async () => {
      const { data } = await api.get<LoanDocument[]>(`/loans/${loanId}/documents`);
      return data;
    },
    enabled: !!loanId,
  });
}

export function useDocumentCategories() {
  return useQuery({
    queryKey: ['document-categories'],
    queryFn: async () => {
      const { data } = await api.get<DocumentCategory[]>('/documents/categories');
      return data;
    },
    staleTime: Infinity,
  });
}

export function invalidateLoanDocumentQueries(qc: QueryClient, loanId: string) {
  qc.invalidateQueries({ queryKey: ['loans', loanId, 'documents'] });
  qc.invalidateQueries({ queryKey: ['loan-documents', loanId] });
  qc.invalidateQueries({ queryKey: ['document-checklist', loanId] });
  qc.invalidateQueries({ queryKey: ['document-matrix', loanId] });
  qc.invalidateQueries({ queryKey: ['document-timeline', loanId] });
  invalidateLoanActivity(qc, loanId);
}

// Custom endpoint with FormData — keep manual
export function useUploadDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      loanId,
      file,
      category,
    }: {
      loanId: string;
      file: File;
      category: string;
    }) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('category', category);
      const { data } = await api.post<LoanDocument>(`/loans/${loanId}/documents/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data;
    },
    onSuccess: (_data, vars) => {
      invalidateLoanDocumentQueries(qc, vars.loanId);
    },
  });
}

interface CreateDocumentInput {
  loanId: string;
  name: string;
  type: string;
  category: string;
}

// Custom endpoint — keep manual (requires loanId extraction)
export function useCreateDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ loanId, ...data }: CreateDocumentInput) => {
      const { data: doc } = await api.post(`/loans/${loanId}/documents`, data);
      return doc;
    },
    onSuccess: (_data, vars) => {
      invalidateLoanDocumentQueries(qc, vars.loanId);
    },
  });
}

// Custom delete endpoint at /documents/{docId} — keep manual
export function useDeleteDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ docId, loanId }: { docId: string; loanId: string }) => {
      await api.delete(`/documents/${docId}`);
      return loanId;
    },
    onSuccess: (_data, vars) => {
      invalidateLoanDocumentQueries(qc, vars.loanId);
    },
  });
}

/** Get document download URL */
export function getDocumentDownloadUrl(docId: string): string {
  return `/api/v1/documents/${docId}/download`;
}
